import React, { useState, useEffect } from 'react';
import { hybridDashboardService } from '../services/hybridDashboardService';
import { supabaseService } from '../services/supabaseService';
import { updateDoc, doc, collection, getDocs, addDoc, serverTimestamp, query, orderBy, deleteDoc } from 'firebase/firestore';
import { db } from '../services/firebaseService';
import { AppUser } from '../types';
import { Timestamp } from 'firebase/firestore';


const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersToday: 0,
    newFemaleUsersToday: 0,
    newMaleUsersToday: 0,
    premiumUsers: 0,
    newPremiumUsersToday: 0,
    pendingGiftCardApplications: 0,
    dailyRevenue: 0,
    monthlyRevenue: 0,
    totalRevenue: 0,
    voiceCallDailyRevenue: 0,
    voiceCallDailySalesCount: 0,
  });

  const [recentUsers, setRecentUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'supabase' | 'firebase' | 'hybrid'>('supabase');
  
  // 📸 Yüklenemeyen görselleri takip et
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  
  // 🖼️ Tam boyutlu görsel modal
  const [selectedImage, setSelectedImage] = useState<{ url: string; name: string } | null>(null);


  const [quickStats, setQuickStats] = useState({
    totalMatches: 0, // Gerçek verilerle güncellenecek
    pendingApplications: 0,
    totalRevenue: 0 // Gerçek verilerle güncellenecek
  });

  // 💰 Manuel Ciro State'leri
  const [manualRevenue, setManualRevenue] = useState(0);
  const [manualRevenueHistory, setManualRevenueHistory] = useState<Array<{id: string, amount: number, note: string, createdAt: any}>>([]);
  const [showManualRevenueModal, setShowManualRevenueModal] = useState(false);
  const [newManualAmount, setNewManualAmount] = useState('');
  const [newManualNote, setNewManualNote] = useState('');
  const [manualRevenueLoading, setManualRevenueLoading] = useState(false);

  // 💰 Manuel Ciro Yükle
  const loadManualRevenue = async () => {
    try {
      const manualRevenueRef = collection(db, 'manual_revenue');
      const q = query(manualRevenueRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const history: Array<{id: string, amount: number, note: string, createdAt: any}> = [];
      let total = 0;
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        history.push({
          id: doc.id,
          amount: data.amount || 0,
          note: data.note || '',
          createdAt: data.createdAt
        });
        total += data.amount || 0;
      });
      
      setManualRevenueHistory(history);
      setManualRevenue(total);
      console.log('💰 Manuel ciro yüklendi:', total);
    } catch (error) {
      console.error('❌ Manuel ciro yüklenemedi:', error);
    }
  };

  // 💰 Manuel Ciro Ekle
  const addManualRevenue = async () => {
    const amount = parseFloat(newManualAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Geçerli bir tutar girin!');
      return;
    }

    setManualRevenueLoading(true);
    try {
      const manualRevenueRef = collection(db, 'manual_revenue');
      await addDoc(manualRevenueRef, {
        amount: amount,
        note: newManualNote || 'Manuel ciro',
        createdAt: serverTimestamp()
      });

      // Yeniden yükle
      await loadManualRevenue();
      
      // Formu temizle
      setNewManualAmount('');
      setNewManualNote('');
      setShowManualRevenueModal(false);
      
      alert(`✅ ₺${amount.toLocaleString()} manuel ciro eklendi!`);
    } catch (error) {
      console.error('❌ Manuel ciro eklenemedi:', error);
      alert('Hata: Manuel ciro eklenemedi!');
    } finally {
      setManualRevenueLoading(false);
    }
  };

  // 💰 Manuel Ciro Sil
  const deleteManualRevenue = async (id: string) => {
    if (!window.confirm('Bu manuel ciroyu silmek istediğinize emin misiniz?')) return;
    
    try {
      await deleteDoc(doc(db, 'manual_revenue', id));
      await loadManualRevenue();
      alert('✅ Manuel ciro silindi!');
    } catch (error) {
      console.error('❌ Manuel ciro silinemedi:', error);
      alert('Hata: Manuel ciro silinemedi!');
    }
  };

  // Tarih formatla
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Bilinmiyor';
    
    try {
      let date;
      if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      } else if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      return 'Bilinmiyor';
    }
  };

  // Kullanıcı durumu etiketini al (basit versiyon)
  const getUserStatus = (user: AppUser) => {
    if (!user.isActive) return 'Pasif';
    return 'Aktif';
  };



  // 🔄 HYBRID Veri yükleme
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔄 HYBRID: Dashboard verisi yükleniyor...');

        // 🔄 HYBRID: Dashboard istatistiklerini ve son kullanıcıları hybrid servis ile yükle
        const [hybridStats, hybridRecentUsers, newPremiumToday] = await Promise.all([
          hybridDashboardService.getDashboardStats(),
          hybridDashboardService.getRecentUsers(10), // Son 10 kullanıcı (daha fazla göster)
          supabaseService.getTodayPremiumSubscribersCount().catch(() => 0) // Bugün abone olan
        ]);

        console.log('✅ HYBRID: Dashboard verisi yüklendi:', {
          dataSource: hybridStats.dataSource,
          totalUsers: hybridStats.totalUsers,
          errors: hybridStats.errors
        });

        // Veri kaynağını kaydet
        setDataSource(hybridStats.dataSource);

        setStats({
          totalUsers: hybridStats.totalUsers,
          activeUsers: hybridStats.activeUsers,
          newUsersToday: hybridStats.newUsersToday,
          newFemaleUsersToday: (hybridStats as any).newFemaleUsersToday || 0,
          newMaleUsersToday: (hybridStats as any).newMaleUsersToday || 0,
          premiumUsers: hybridStats.premiumUsers,
          newPremiumUsersToday: newPremiumToday,
          pendingGiftCardApplications: hybridStats.pendingGiftCardApplications,
          dailyRevenue: hybridStats.dailyRevenue || 0,
          monthlyRevenue: hybridStats.monthlyRevenue,
          totalRevenue: hybridStats.totalRevenue || 0,
          voiceCallDailyRevenue: (hybridStats as any).voiceCallDailyRevenue || 0,
          voiceCallDailySalesCount: (hybridStats as any).voiceCallDailySalesCount || 0,
        });

        setRecentUsers(hybridRecentUsers.users);

        // Hızlı istatistikleri güncelle
        setQuickStats({
          totalMatches: hybridStats.totalMatches,
          pendingApplications: hybridStats.pendingGiftCardApplications,
          totalRevenue: hybridStats.totalRevenue || 0
        });

        // Debug için son kullanıcıları konsola yazdır
        console.log('👥 RECENT USERS DEBUG:', hybridRecentUsers.users.map(u => ({
          name: u.name,
          email: u.email,
          createdAt: u.createdAt
        })));

        // Hata varsa konsola yazdır ama kullanıcıya gösterme (veriler yüklendiyse)
        if (hybridStats.errors && hybridStats.errors.length > 0) {
          console.warn('🔄 HYBRID: Bazı veriler fallback ile yüklendi:', hybridStats.errors);
        }

      } catch (err) {
        console.error('❌ HYBRID: Dashboard verilerini yükleme hatası:', err);
        setError('Veriler yüklenirken bir hata oluştu. Hem Supabase hem Firebase erişilemiyor.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    loadManualRevenue(); // 💰 Manuel ciro yükle
    
    // 🚀 PERFORMANS: Otomatik yenileme kaldırıldı - manuel yenileme tercih edilir
  }, []);

  // QuickStats state güncellemesi için (artık yukarıda tanımlı)

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #F78D93',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p style={{ color: '#666' }}>Dashboard verileri yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '2rem',
        backgroundColor: '#f8d7da',
        borderRadius: '8px',
        color: '#721c24'
      }}>
        <h3>⚠️ Hata</h3>
        <p>{error}</p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button 
            onClick={async () => {
              console.log('🔄 MANUAL REFRESH: Dashboard manuel olarak yenileniyor...');
              setLoading(true);
              try {
                const [hybridStats, hybridRecentUsers, newPremiumToday] = await Promise.all([
                  hybridDashboardService.getDashboardStats(),
                  hybridDashboardService.getRecentUsers(10), // Son 10 kullanıcı (daha fazla göster)
                  supabaseService.getTodayPremiumSubscribersCount().catch(() => 0)
                ]);

                setDataSource(hybridStats.dataSource);
                setStats({
                  totalUsers: hybridStats.totalUsers,
                  activeUsers: hybridStats.activeUsers,
                  newUsersToday: hybridStats.newUsersToday,
                  newFemaleUsersToday: (hybridStats as any).newFemaleUsersToday || 0,
                  newMaleUsersToday: (hybridStats as any).newMaleUsersToday || 0,
                  premiumUsers: hybridStats.premiumUsers,
                  newPremiumUsersToday: newPremiumToday,
                  pendingGiftCardApplications: hybridStats.pendingGiftCardApplications,
                  dailyRevenue: hybridStats.dailyRevenue || 0,
                  monthlyRevenue: hybridStats.monthlyRevenue,
                  totalRevenue: hybridStats.totalRevenue || 0,
                  voiceCallDailyRevenue: (hybridStats as any).voiceCallDailyRevenue || 0,
                  voiceCallDailySalesCount: (hybridStats as any).voiceCallDailySalesCount || 0,
                });
                setRecentUsers(hybridRecentUsers.users);
                setQuickStats({
                  totalMatches: hybridStats.totalMatches,
                  pendingApplications: hybridStats.pendingGiftCardApplications,
                  totalRevenue: hybridStats.totalRevenue || 0
                });
                console.log(`✅ MANUAL REFRESH: Dashboard yenilendi! Son kullanıcılar: ${hybridRecentUsers.users.length}`);
                console.log('👥 Recent Users:', hybridRecentUsers.users.map(u => ({ 
                  name: u.name, 
                  email: u.email, 
                  createdAt: u.createdAt 
                })));
              } catch (error) {
                console.error('❌ MANUAL REFRESH: Hata!', error);
                setError('Yenileme sırasında hata oluştu');
              } finally {
                setLoading(false);
              }
            }}
          style={{
            backgroundColor: '#F78D93',
            color: 'white',
            border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🔄 Dashboard Yenile
          </button>
          <button 
            onClick={async () => {
              console.log('🔧 MANUAL TEST: Supabase bağlantısı test ediliyor...');
              try {
                const result = await hybridDashboardService.getTotalUsersCount();
                console.log('✅ MANUAL TEST: Başarılı!', result);
                alert(`✅ Test Başarılı!\nKullanıcı sayısı: ${result.count}\nKaynak: ${result.source}`);
              } catch (error) {
                console.error('❌ MANUAL TEST: Hata!', error);
                alert(`❌ Test Hatası!\n${(error as any)?.message || error}`);
              }
            }}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            🧪 Supabase Test
        </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* 🔄 HYBRID System Status Banner */}
      <div style={{
        backgroundColor: dataSource === 'supabase' ? '#d4edda' : dataSource === 'firebase' ? '#f8d7da' : '#fff3cd',
        border: `1px solid ${dataSource === 'supabase' ? '#c3e6cb' : dataSource === 'firebase' ? '#f5c6cb' : '#ffeaa7'}`,
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            fontSize: '1.5rem',
          }}>
            {dataSource === 'supabase' && '🟢'}
            {dataSource === 'firebase' && '🔴'}
            {dataSource === 'hybrid' && '🟡'}
          </div>
          <div>
            <h4 style={{ 
              margin: 0, 
              color: dataSource === 'supabase' ? '#155724' : dataSource === 'firebase' ? '#721c24' : '#856404',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}>
              Hybrid Database System - {dataSource === 'supabase' ? 'Supabase Primary' : dataSource === 'firebase' ? 'Firebase Fallback' : 'Mixed Sources'}
            </h4>
            <p style={{ 
              margin: 0, 
              color: dataSource === 'supabase' ? '#155724' : dataSource === 'firebase' ? '#721c24' : '#856404',
              fontSize: '0.9rem'
            }}>
              {dataSource === 'supabase' && 'Tüm veriler Supabase\'den başarıyla yüklendi'}
              {dataSource === 'firebase' && 'Supabase erişilemiyor, Firebase fallback aktif'}
              {dataSource === 'hybrid' && 'Bazı veriler Supabase\'den, bazıları Firebase\'den yüklendi'}
            </p>
          </div>
        </div>
        <div style={{
          backgroundColor: dataSource === 'supabase' ? '#28a745' : dataSource === 'firebase' ? '#dc3545' : '#ffc107',
          color: dataSource === 'hybrid' ? '#212529' : 'white',
          padding: '0.5rem 1rem',
          borderRadius: '20px',
          fontSize: '0.8rem',
          fontWeight: 'bold'
        }}>
          {dataSource === 'supabase' && 'OPTIMAL'}
          {dataSource === 'firebase' && 'FALLBACK'}
          {dataSource === 'hybrid' && 'MIXED'}
        </div>
      </div>

      {/* Dashboard Header with Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem',
        padding: '1rem 0'
      }}>
        <div>
          <h1 style={{ 
            color: '#2c3e50', 
            margin: '0 0 0.5rem 0',
            fontSize: '1.8rem',
            fontWeight: '600'
          }}>
            📊 Dashboard
          </h1>
          <p style={{ color: '#7f8c8d', margin: 0 }}>
            Datezy Admin Panel - Anlık Veriler ve İstatistikler
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button 
            onClick={async () => {
              console.log('🔄 MANUAL REFRESH: Dashboard manuel olarak yenileniyor...');
              setLoading(true);
              try {
                const [hybridStats, hybridRecentUsers, newPremiumToday] = await Promise.all([
                  hybridDashboardService.getDashboardStats(),
                  hybridDashboardService.getRecentUsers(10), // Son 10 kullanıcı (daha fazla göster)
                  supabaseService.getTodayPremiumSubscribersCount().catch(() => 0)
                ]);

                setDataSource(hybridStats.dataSource);
                setStats({
                  totalUsers: hybridStats.totalUsers,
                  activeUsers: hybridStats.activeUsers,
                  newUsersToday: hybridStats.newUsersToday,
                  newFemaleUsersToday: (hybridStats as any).newFemaleUsersToday || 0,
                  newMaleUsersToday: (hybridStats as any).newMaleUsersToday || 0,
                  premiumUsers: hybridStats.premiumUsers,
                  newPremiumUsersToday: newPremiumToday,
                  pendingGiftCardApplications: hybridStats.pendingGiftCardApplications,
                  dailyRevenue: hybridStats.dailyRevenue || 0,
                  monthlyRevenue: hybridStats.monthlyRevenue,
                  totalRevenue: hybridStats.totalRevenue || 0,
                  voiceCallDailyRevenue: (hybridStats as any).voiceCallDailyRevenue || 0,
                  voiceCallDailySalesCount: (hybridStats as any).voiceCallDailySalesCount || 0,
                });
                setRecentUsers(hybridRecentUsers.users);
                setQuickStats({
                  totalMatches: hybridStats.totalMatches,
                  pendingApplications: hybridStats.pendingGiftCardApplications,
                  totalRevenue: hybridStats.totalRevenue || 0
                });
                console.log(`✅ MANUAL REFRESH: Dashboard yenilendi! Son kullanıcılar: ${hybridRecentUsers.users.length}`);
                console.log('👥 Recent Users:', hybridRecentUsers.users.map(u => ({ 
                  name: u.name, 
                  email: u.email, 
                  createdAt: u.createdAt 
                })));
              } catch (error) {
                console.error('❌ MANUAL REFRESH: Hata!', error);
                setError('Yenileme sırasında hata oluştu');
              } finally {
                setLoading(false);
              }
            }}
            style={{
              backgroundColor: '#F78D93',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            🔄 Dashboard Yenile
          </button>
          <button 
            onClick={async () => {
              console.log('🔧 MANUAL TEST: Supabase bağlantısı test ediliyor...');
              try {
                const result = await hybridDashboardService.getTotalUsersCount();
                console.log('✅ MANUAL TEST: Başarılı!', result);
                alert(`✅ Test Başarılı!\nKullanıcı sayısı: ${result.count}\nKaynak: ${result.source}`);
              } catch (error) {
                console.error('❌ MANUAL TEST: Hata!', error);
                alert(`❌ Test Hatası!\n${(error as any)?.message || error}`);
              }
            }}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            🧪 Supabase Test
          </button>
        </div>
      </div>

      {/* Main Statistics Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#F78D93',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            👥
          </div>
          <div>
            <h3 style={{ 
              color: '#F78D93', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              TOPLAM KULLANICI
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              {stats.totalUsers.toLocaleString()}
            </p>
            <p style={{ 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase (Fallback)'}
              {dataSource === 'hybrid' && '🟡 Hybrid (Mixed Sources)'}
            </p>
          </div>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#27ae60',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            🟢
          </div>
          <div>
            <h3 style={{ 
              color: '#27ae60', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              AKTİF KULLANICI
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              {stats.activeUsers.toLocaleString()}
            </p>
            <p style={{ 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase (Fallback)'}
              {dataSource === 'hybrid' && '🟡 Hybrid (Mixed Sources)'}
            </p>
          </div>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#3498db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            📈
          </div>
          <div>
            <h3 style={{ 
              color: '#3498db', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              BUGÜNKÜ YENİ ÜYELER
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              {stats.newUsersToday}
            </p>
            <p style={{
              margin: 0,
              fontSize: '0.85rem',
              color: '#666',
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap'
            }}>
              <span><strong>Kadın:</strong> {stats.newFemaleUsersToday.toLocaleString()}</span>
              <span><strong>Erkek:</strong> {stats.newMaleUsersToday.toLocaleString()}</span>
            </p>
            <p style={{ 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase (Fallback)'}
              {dataSource === 'hybrid' && '🟡 Hybrid (Mixed Sources)'}
            </p>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#f39c12',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            💎
          </div>
          <div>
            <h3 style={{ 
              color: '#f39c12', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              PREMİUM KULLANICILAR
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              {stats.premiumUsers.toLocaleString()}
            </p>
            <p style={{ 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase (Fallback)'}
              {dataSource === 'hybrid' && '🟡 Hybrid (Mixed Sources)'}
            </p>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '2px solid #9b59b6'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#9b59b6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            ⭐
          </div>
          <div>
            <h3 style={{ 
              color: '#9b59b6', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              BUGÜN ABONE OLAN
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              {stats.newPremiumUsersToday}
            </p>
            <p style={{ 
              color: '#27ae60', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              🟢 Supabase (sales)
            </p>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#e74c3c',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            📅
          </div>
          <div>
            <h3 style={{ 
              color: '#e74c3c', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              GÜNLÜK GELİR
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              ₺{stats.dailyRevenue?.toLocaleString() || '0'}
            </p>
            <p style={{ 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase (Fallback)'}
              {dataSource === 'hybrid' && '🟡 Hybrid (Mixed Sources)'}
            </p>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#8e44ad',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            🎙️
          </div>
          <div>
            <h3 style={{ 
              color: '#8e44ad', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              SESLİ ARAMA PAKETİ (BUGÜN)
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              ₺{stats.voiceCallDailyRevenue?.toLocaleString() || '0'}
            </p>
            <p style={{ 
              color: '#666', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span style={{ fontWeight: 600 }}>Satış:</span> {stats.voiceCallDailySalesCount?.toLocaleString() || '0'}
            </p>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#9b59b6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            📊
          </div>
          <div>
            <h3 style={{ 
              color: '#9b59b6', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              TOPLAM GELİR
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              ₺{stats.totalRevenue?.toLocaleString() || '0'}
            </p>
            <p style={{ 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase (Fallback)'}
              {dataSource === 'hybrid' && '🟡 Hybrid (Mixed Sources)'}
            </p>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#27ae60',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            💵
          </div>
          <div>
            <h3 style={{ 
              color: '#27ae60', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              AYLIK GELİR
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              ₺{stats.monthlyRevenue.toLocaleString()}
            </p>
            <p style={{ 
              color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12', 
              fontSize: '0.85rem', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              {dataSource === 'supabase' && '🟢 Supabase'}
              {dataSource === 'firebase' && '🔴 Firebase (Fallback)'}
              {dataSource === 'hybrid' && '🟡 Hybrid (Mixed Sources)'}
            </p>
          </div>
        </div>

        {/* 💰 Manuel Ciro Kartı */}
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          border: '2px solid #16a085',
          position: 'relative'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#16a085',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '1.5rem',
            fontSize: '24px'
          }}>
            ➕
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ 
              color: '#16a085', 
              margin: '0 0 0.5rem 0',
              fontSize: '0.9rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              MANUEL CİRO
            </h3>
            <p style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              margin: '0 0 0.25rem 0',
              color: '#2c3e50'
            }}>
              ₺{manualRevenue.toLocaleString()}
            </p>
            <p style={{ 
              color: '#666', 
              fontSize: '0.85rem', 
              margin: 0
            }}>
              {manualRevenueHistory.length} kayıt
            </p>
          </div>
          <button
            onClick={() => setShowManualRevenueModal(true)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              backgroundColor: '#16a085',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ➕ Ciro Ekle
          </button>
        </div>

        {/* Bekleyen Hediye Kartı kartı kaldırıldı (istek üzerine) */}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
        {/* Recent Users Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: '#fafafa'
          }}>
            <h3 style={{ 
              margin: 0, 
              color: '#2c3e50', 
              fontSize: '1.2rem',
              fontWeight: '600'
            }}>
              Son Kaydolan Kullanıcılar
              <span style={{ 
                fontSize: '0.8rem', 
                color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12',
                marginLeft: '0.5rem',
                backgroundColor: dataSource === 'supabase' ? '#d4edda' : dataSource === 'firebase' ? '#f8d7da' : '#fff3cd',
                padding: '0.25rem 0.5rem',
                borderRadius: '12px',
                border: `1px solid ${dataSource === 'supabase' ? '#c3e6cb' : dataSource === 'firebase' ? '#f5c6cb' : '#ffeaa7'}`
              }}>
                {dataSource === 'supabase' && '🟢 Supabase'}
                {dataSource === 'firebase' && '🔴 Firebase (Fallback)'}
                {dataSource === 'hybrid' && '🟡 Hybrid (Mixed Sources)'}
              </span>
            </h3>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {recentUsers.length === 0 ? (
              <div style={{ 
                padding: '2rem', 
                textAlign: 'center', 
                color: '#666' 
              }}>
                <p>Henüz kullanıcı bulunamadı</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ 
                      padding: '1rem 1.5rem', 
                      textAlign: 'left', 
                      color: '#666', 
                      fontSize: '0.85rem', 
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Kullanıcı
                    </th>
                    <th style={{ 
                      padding: '1rem 1.5rem', 
                      textAlign: 'left', 
                      color: '#666', 
                      fontSize: '0.85rem', 
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      E-posta
                    </th>
                    <th style={{ 
                      padding: '1rem 1.5rem', 
                      textAlign: 'left', 
                      color: '#666', 
                      fontSize: '0.85rem', 
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Kayıt Tarihi
                    </th>
                    <th style={{ 
                      padding: '1rem 1.5rem', 
                      textAlign: 'left', 
                      color: '#666', 
                      fontSize: '0.85rem', 
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Durum
                    </th>
                    <th style={{ 
                      padding: '1rem 1.5rem', 
                      textAlign: 'center', 
                      color: '#666', 
                      fontSize: '0.85rem', 
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.map((user, index) => (
                    <tr key={user.id} style={{ 
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                    }}>
                      <td style={{ padding: '1rem 1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {/* Profil resmi - state-based fallback */}
                          {(() => {
                            // Önce photos array'ini, sonra diğer alanları kontrol et
                            const profileImage = user.photos && user.photos.length > 0 
                              ? user.photos[0]
                              : user.photoURL || user.profilePictureUrl || user.profilePicUrl || user.googlePhotoURL || null;
                            
                            const hasImage = profileImage && !failedImages.has(user.id);
                            
                            return hasImage ? (
                              <img
                                src={profileImage}
                                alt={user.name || 'Kullanıcı'}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  marginRight: '1rem',
                                  border: '2px solid #F78D93',
                                  cursor: 'pointer',
                                  transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                onClick={() => setSelectedImage({ url: profileImage, name: user.name || 'Kullanıcı' })}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.transform = 'scale(1.1)';
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(247, 141, 147, 0.4)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.transform = 'scale(1)';
                                  e.currentTarget.style.boxShadow = 'none';
                                }}
                                onError={() => {
                                  // Fotoğraf yüklenemezse state'e ekle
                                  setFailedImages(prev => new Set(prev).add(user.id));
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: '#F78D93',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '1rem',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: 'white'
                              }}>
                                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                              </div>
                            );
                          })()}
                          <span style={{ fontWeight: '500', color: '#2c3e50' }}>
                            {user.name || 'İsimsiz Kullanıcı'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', color: '#666' }}>
                        {user.email || 'E-posta yok'}
                        {!user.email && (
                          <button
                            onClick={() => {
                                                             const email = prompt('Email adresi girin:');
                               if (email) {
                                 updateDoc(doc(db, 'users', user.id), { email })
                                   .then(() => {
                                     alert('Email güncellendi!');
                                     window.location.reload();
                                   })
                                   .catch((err: any) => alert('Hata: ' + err));
                               }
                            }}
                            style={{
                              marginLeft: '0.5rem',
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#3498db',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              cursor: 'pointer'
                            }}
                          >
                            Email Ekle
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '1rem 1.5rem', color: '#666' }}>
                        {formatDate(user.createdAt)}
                      </td>
                      <td style={{ padding: '1rem 1.5rem' }}>
                        <span style={{
                          backgroundColor: getUserStatus(user) === 'Aktif' ? '#27ae60' : '#e74c3c',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}>
                          {getUserStatus(user)}
                        </span>
                      </td>
                      <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button 
                            title="Kullanıcıyı Görüntüle"
                            style={{
                              backgroundColor: '#3498db',
                              color: 'white',
                              border: 'none',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            👁️
                          </button>
                          <button 
                            title="Kullanıcıyı Düzenle"
                            style={{
                              backgroundColor: '#95a5a6',
                              color: 'white',
                              border: 'none',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            ✏️
                          </button>
                          <button 
                            title={user.isActive ? 'Kullanıcıyı Pasifleştir' : 'Kullanıcıyı Aktifleştir'}
                            style={{
                              backgroundColor: user.isActive ? '#e74c3c' : '#27ae60',
                              color: 'white',
                              border: 'none',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            {user.isActive ? '🚫' : '✅'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right Sidebar - Quick Stats and System Status */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Quick Statistics */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            padding: '1.5rem'
          }}>
            <h3 style={{ 
              margin: '0 0 1.5rem 0', 
              color: '#2c3e50',
              fontSize: '1.1rem',
              fontWeight: '600'
            }}>
              Hızlı İstatistikler
              <div style={{
                fontSize: '0.7rem',
                color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12',
                marginTop: '0.25rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: dataSource === 'supabase' ? '#d4edda' : dataSource === 'firebase' ? '#f8d7da' : '#fff3cd',
                borderRadius: '8px',
                border: `1px solid ${dataSource === 'supabase' ? '#c3e6cb' : dataSource === 'firebase' ? '#f5c6cb' : '#ffeaa7'}`,
                textAlign: 'center'
              }}>
                {dataSource === 'supabase' && '🟢 Supabase Primary'}
                {dataSource === 'firebase' && '🔴 Firebase Fallback'}
                {dataSource === 'hybrid' && '🟡 Hybrid Mixed Sources'}
              </div>
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>Toplam Eşleşme</span>
                <span style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                  {quickStats.totalMatches.toLocaleString()}
                </span>
              </div>
              <div style={{ 
                fontSize: '0.7rem', 
                color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12',
                textAlign: 'center',
                marginTop: '0.25rem'
              }}>
                {dataSource === 'supabase' && '🟢 Supabase'}
                {dataSource === 'firebase' && '🔴 Firebase'}
                {dataSource === 'hybrid' && '🟡 Hybrid'}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>Bekleyen Başvuru</span>
                <span style={{ 
                  fontWeight: 'bold', 
                  color: '#f39c12',
                  backgroundColor: '#fff3cd',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.8rem'
                }}>
                  {quickStats.pendingApplications}
                </span>
              </div>
              <div style={{ 
                fontSize: '0.7rem', 
                color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12',
                textAlign: 'center',
                marginTop: '0.25rem'
              }}>
                {dataSource === 'supabase' && '🟢 Supabase'}
                {dataSource === 'firebase' && '🔴 Firebase'}
                {dataSource === 'hybrid' && '🟡 Hybrid'}
              </div>
            </div>

            <div style={{ marginBottom: '0' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ color: '#666', fontSize: '0.9rem' }}>Toplam Gelir</span>
                <span style={{ 
                  fontWeight: 'bold', 
                  color: '#27ae60',
                  fontSize: '1.1rem'
                }}>
                  ₺{quickStats.totalRevenue.toLocaleString()}
                </span>
              </div>
              <div style={{ 
                fontSize: '0.7rem', 
                color: dataSource === 'supabase' ? '#27ae60' : dataSource === 'firebase' ? '#e74c3c' : '#f39c12',
                textAlign: 'center',
                marginTop: '0.25rem'
              }}>
                {dataSource === 'supabase' && '🟢 Supabase'}
                {dataSource === 'firebase' && '🔴 Firebase'}
                {dataSource === 'hybrid' && '🟡 Hybrid'}
              </div>
            </div>
          </div>

          {/* System Status */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            padding: '1.5rem',
            background: dataSource === 'supabase' 
              ? 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)' 
              : dataSource === 'firebase' 
                ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                : 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
            color: 'white'
          }}>
            <h3 style={{ 
              margin: '0 0 1.5rem 0', 
              fontSize: '1.1rem',
              fontWeight: '600'
            }}>
              Hybrid Database System
              <div style={{
                fontSize: '0.8rem',
                marginTop: '0.25rem',
                opacity: 0.9
              }}>
                {dataSource === 'supabase' && 'Supabase Primary Active'}
                {dataSource === 'firebase' && 'Firebase Fallback Active'}
                {dataSource === 'hybrid' && 'Mixed Sources Active'}
              </div>
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.9rem' }}>Supabase Bağlantısı</span>
                <div style={{
                  width: '100px',
                  height: '6px',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: dataSource === 'firebase' ? '0%' : '100%',
                    height: '100%',
                    backgroundColor: 'white',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                {dataSource === 'firebase' ? '❌ Erişilemiyor' : '✅ Aktif'}
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.9rem' }}>Firebase Bağlantısı</span>
                <div style={{
                  width: '100px',
                  height: '6px',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'white',
                    borderRadius: '3px'
                  }}></div>
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>🔥 Aktif (Fallback Ready)</div>
            </div>

            <div style={{ marginBottom: '0' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span style={{ fontSize: '0.9rem' }}>Veri Senkronizasyonu</span>
                <div style={{
                  width: '100px',
                  height: '6px',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: dataSource === 'hybrid' ? '75%' : '95%',
                    height: '100%',
                    backgroundColor: 'white',
                    borderRadius: '3px'
                  }}></div>
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                {dataSource === 'supabase' && '📊 Optimal Performance'}
                {dataSource === 'firebase' && '📊 Fallback Mode'}
                {dataSource === 'hybrid' && '⚠️ Mixed Mode'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* 🖼️ Tam Boyutlu Görsel Modal */}
      {selectedImage && (
        <div
          onClick={() => setSelectedImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            cursor: 'pointer',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          {/* Kapat Butonu */}
          <button
            onClick={() => setSelectedImage(null)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              fontSize: '2rem',
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          >
            ✕
          </button>

          {/* Kullanıcı Adı */}
          <h3 style={{
            color: 'white',
            marginBottom: '1rem',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            {selectedImage.name}
          </h3>

          {/* Tam Boyutlu Görsel */}
          <img
            src={selectedImage.url}
            alt={selectedImage.name}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '80vh',
              borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              objectFit: 'contain'
            }}
          />

          {/* Bilgi */}
          <p style={{
            color: 'rgba(255,255,255,0.6)',
            marginTop: '1rem',
            fontSize: '0.9rem'
          }}>
            Kapatmak için herhangi bir yere tıklayın
          </p>
        </div>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>

      {/* 💰 Manuel Ciro Ekleme Modalı */}
      {showManualRevenueModal && (
        <div
          onClick={() => setShowManualRevenueModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              width: '500px',
              maxWidth: '90vw',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, color: '#2c3e50' }}>💰 Manuel Ciro Ekle</h2>
              <button
                onClick={() => setShowManualRevenueModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ✕
              </button>
            </div>

            {/* Form */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                Tutar (₺)
              </label>
              <input
                type="number"
                value={newManualAmount}
                onChange={(e) => setNewManualAmount(e.target.value)}
                placeholder="Örn: 1000"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1.1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                Not (İsteğe bağlı)
              </label>
              <input
                type="text"
                value={newManualNote}
                onChange={(e) => setNewManualNote(e.target.value)}
                placeholder="Örn: Elden satış, Reklam geliri vb."
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <button
              onClick={addManualRevenue}
              disabled={manualRevenueLoading}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: manualRevenueLoading ? '#95a5a6' : '#16a085',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: manualRevenueLoading ? 'not-allowed' : 'pointer',
                marginBottom: '1.5rem'
              }}
            >
              {manualRevenueLoading ? '⏳ Ekleniyor...' : '✅ Ciro Ekle'}
            </button>

            {/* Geçmiş */}
            <div>
              <h4 style={{ margin: '0 0 1rem 0', color: '#666', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                📜 Manuel Ciro Geçmişi ({manualRevenueHistory.length})
              </h4>
              
              {manualRevenueHistory.length === 0 ? (
                <p style={{ color: '#999', textAlign: 'center', padding: '1rem' }}>
                  Henüz manuel ciro eklenmemiş
                </p>
              ) : (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {manualRevenueHistory.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        marginBottom: '0.5rem'
                      }}
                    >
                      <div>
                        <strong style={{ color: '#16a085' }}>₺{item.amount.toLocaleString()}</strong>
                        <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                          {item.note}
                        </span>
                        <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                          {item.createdAt ? formatDate(item.createdAt) : 'Yeni'}
                        </div>
                      </div>
                      <button
                        onClick={() => deleteManualRevenue(item.id)}
                        style={{
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage; 